import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, Search, Star, Video, FileDown } from 'lucide-react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { logError } from '@/utils/logger';
import { LinkedInFeed } from '@/components/LinkedInFeed';
import { linkedinPosts } from '@/data/linkedinPosts';
import { BrandHero } from '@/components/brand/BrandKit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMBED_HOSTS = ["www.youtube.com", "youtube.com", "www.youtube-nocookie.com", "player.vimeo.com"];

const safeUrl = (url) => (url && (url.startsWith('/api/uploads/') || /^https?:\/\//i.test(url)) ? url : '');
const safeEmbed = (url) => {
  try { const u = new URL(url); return u.protocol === 'https:' && EMBED_HOSTS.includes(u.hostname) ? url : ''; } catch { return ''; }
};

const CATEGORIES = ["All", "Mobility News", "Traffic & Authority Updates", "Road & Travel Guides", "Fleet & Corporate Mobility", "Company Updates", "Press Releases", "Awards", "Fleet", "Sustainability"];
const CATEGORY_AR = { "All": "الكل", "Mobility News": "أخبار التنقل", "Traffic & Authority Updates": "تحديثات المرور والهيئات", "Road & Travel Guides": "أدلة الطرق والسفر", "Fleet & Corporate Mobility": "الأساطيل وتنقل الشركات", "Company Updates": "أخبار المجموعة", "Press Releases": "بيانات صحفية", "Awards": "الجوائز", "Fleet": "الأسطول", "Sustainability": "الاستدامة" };

const T = {
  en: { title: 'Insights & News | Eurogulf Mobility', chip: 'News & Insights', h1: 'Stories from the group that keeps the UAE moving.', lead: 'Press releases, industry insight, fleet updates and awards from Eurogulf Mobility Group and its seven brands.', search: 'Search articles...', featured: 'Featured', loading: 'Loading articles...', none: 'No articles found', noneHint: 'Try adjusting your search or filters.', count: (n) => `${n} article${n !== 1 ? 's' : ''}`, pdf: 'Download PDF', cat: (c) => c, testPrefix: '' },
  ar: { title: 'المركز الإعلامي | الأخبار والرؤى | مجموعة يوروجلف للتنقل', chip: 'الأخبار والرؤى', h1: 'قصص من المجموعة التي تحرّك الإمارات.', lead: 'البيانات الصحفية، ورؤى القطاع، وتحديثات الأسطول، والجوائز من مجموعة يوروجلف للتنقل وعلاماتها السبع.', search: 'ابحث في المقالات...', featured: 'مقالات مميزة', loading: 'جارٍ تحميل المقالات...', none: 'لا توجد مقالات', noneHint: 'جرّب تعديل البحث أو الفلاتر.', count: (n) => `${n} مقالة`, pdf: 'تحميل الملف (PDF)', cat: (c) => CATEGORY_AR[c] || c, testPrefix: 'ar-' },
};

export default function MediaCenterPage({ lang = 'en' }) {
  const t = T[lang];
  const isAr = lang === 'ar';
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get('category') || 'All');
  const [searchQuery, setSearchQuery] = useState("");
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { document.title = !isAr && filter !== "All" ? `${filter} | Eurogulf Mobility Insights` : t.title; }, [filter, isAr, t.title]);

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) setFilter((current) => (cat !== current ? cat : current));
  }, [searchParams]);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const params = { limit: 50 };
        if (filter !== "All") params.category = filter;
        if (searchQuery) params.search = searchQuery;
        const { data } = await axios.get(`${API}/articles`, { params });
        setArticles(data.articles.filter((a) => a.published !== false));
        setTotal(data.total);
      } catch (err) {
        logError('Articles', err);
      } finally {
        setLoading(false);
      }
    };
    const debounce = setTimeout(fetchArticles, 300);
    return () => clearTimeout(debounce);
  }, [filter, searchQuery]);

  const featured = articles.filter((a) => a.featured);
  const regular = articles.filter((a) => !a.featured);
  const toggle = (id) => setExpandedId(expandedId === id ? null : id);
  const p = t.testPrefix;

  return (
    <div data-testid={isAr ? 'media-center-page-ar' : 'media-center-page'} dir={isAr ? 'rtl' : 'ltr'}>
      <BrandHero testid={`${p}media-hero`} chip={t.chip} title={t.h1} lead={t.lead} image="/generated/about-group-cityscape.jpg" position="center 60%" />

      <section className="bg-white border-b border-black/5 py-4 sticky top-16 lg:top-[76px] z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-md w-full">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
            <input data-testid={`${p}media-search`} type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={t.search}
              className="w-full rounded-lg bg-[#FAFAFA] border border-black/10 text-[#121212] placeholder:text-[#999] ps-10 pe-4 py-2.5 text-sm font-body focus:border-[#EE5A01] focus:bg-white focus:outline-none transition-colors" />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} data-testid={`${p}filter-${c.toLowerCase().replace(/[\s&]+/g, '-')}`} onClick={() => setFilter(c)}
                className={`rounded-full font-heading font-bold text-[11px] px-3 py-1.5 transition-colors ${filter === c ? 'bg-[#EE5A01] text-white' : 'text-[#666666] border border-black/10 hover:border-[#EE5A01]/40 hover:text-[#121212]'}`}>
                {t.cat(c)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="bg-[#FAFAFA] py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="flex items-center gap-2 mb-6 font-heading font-bold text-[11px] tracking-[0.2em] uppercase text-[#EE5A01]"><Star className="w-4 h-4 fill-[#EE5A01]" /> {t.featured}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featured.map((a) => <ArticleCard key={a.id} article={a} ar={isAr} expanded={expandedId === a.id} onToggle={() => toggle(a.id)} pdfLabel={t.pdf} />)}
            </div>
          </div>
        </section>
      )}

      <section data-testid={`${p}articles-grid`} className="bg-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-[#EE5A01] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="font-body text-sm text-[#666666]">{t.loading}</p>
            </div>
          ) : regular.length === 0 && featured.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-heading font-bold text-lg text-[#121212]">{t.none}</p>
              <p className="font-body text-sm text-[#666666] mt-2">{t.noneHint}</p>
            </div>
          ) : (
            <>
              <p className="font-heading font-bold text-[11px] tracking-[0.2em] uppercase text-[#666666] mb-6">{t.count(total)}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {regular.map((a) => <ArticleCard key={a.id} article={a} ar={isAr} expanded={expandedId === a.id} onToggle={() => toggle(a.id)} pdfLabel={t.pdf} />)}
              </div>
            </>
          )}
        </div>
      </section>

      <LinkedInFeed posts={linkedinPosts} lang={lang} testId={`${p}linkedin-feed`} />
    </div>
  );
}

export function ArticleCard({ article, expanded, onToggle, ar = false, pdfLabel }) {
  const a = article;
  const dateStr = new Date(a.created_at).toLocaleDateString(ar ? 'ar-AE' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const title = ar && a.title_ar ? a.title_ar : a.title;
  const body = ar && a.body_ar ? a.body_ar : a.body;
  const textDir = ar && a.title_ar ? 'rtl' : 'ltr';
  const bodyDir = ar && a.body_ar ? 'rtl' : 'ltr';

  return (
    <div data-testid={`article-card-${a.id}`} className="group cursor-pointer overflow-hidden rounded-xl border border-black/[0.06] bg-white transition-all hover:-translate-y-0.5 hover:border-[#EE5A01]/30 hover:shadow-[0_16px_40px_rgba(0,0,0,0.06)]" onClick={onToggle}>
      {safeUrl(a.image_url) && (
        <div className="aspect-[16/9] overflow-hidden">
          <img src={safeUrl(a.image_url)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="rounded-full bg-[#FFF4ED] px-2.5 py-0.5 font-heading font-bold text-[10px] tracking-wider uppercase text-[#EE5A01]">{ar ? (CATEGORY_AR[a.category] || a.category) : a.category}</span>
          {a.featured && <Star className="w-3 h-3 text-[#EE5A01] fill-[#EE5A01]" />}
          {a.video_url && <Video className="w-3 h-3 text-[#999]" />}
        </div>
        <h3 dir={textDir} className={`font-heading font-bold text-base text-[#121212] mb-2 leading-snug group-hover:text-[#EE5A01] transition-colors ${textDir === 'ltr' && ar ? 'text-left' : ''}`}>{title}</h3>
        <div className="flex items-center gap-2 text-[#666666]"><Calendar className="w-3 h-3" /><span className="font-body text-xs">{dateStr}</span></div>
        {expanded && (
          <div className="mt-4 pt-4 border-t border-black/5">
            {body && <div dir={bodyDir} className={`font-body text-sm text-[#555555] leading-relaxed article-body ${bodyDir === 'ltr' && ar ? 'text-left' : ''}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }} />}
            {safeEmbed(a.video_url) && (
              <div className="mt-4 aspect-video overflow-hidden rounded-lg">
                <iframe src={safeEmbed(a.video_url)} title={a.title} className="w-full h-full" allowFullScreen loading="lazy" sandbox="allow-scripts allow-same-origin allow-presentation allow-popups" referrerPolicy="strict-origin-when-cross-origin" />
              </div>
            )}
            {safeUrl(a.pdf_url) && (
              <a href={safeUrl(a.pdf_url)} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 font-heading font-bold text-xs text-[#EE5A01] hover:underline"><FileDown className="w-4 h-4" /> {pdfLabel || (ar ? 'تحميل الملف (PDF)' : 'Download PDF')}</a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
